import React from 'react';
import { motion } from 'framer-motion';

const philosophyPillars = [
  {
    title: 'Speed Over Perfection',
    description: 'We deploy in weeks, not quarters. Your first AI agent goes live before most companies finish their requirements gathering. We ship, measure, optimize — in that order.',
    icon: '⚡',
  },
  {
    title: 'Outcomes Over Activities',
    description: 'We don\'t bill hours. We don\'t measure success by deliverables completed. Every engagement has a defined ROI target. If we\'re not saving you money or making you money, we\'re not doing our job.',
    icon: '📊',
  },
  {
    title: 'Integration Over Innovation',
    description: 'We don\'t build shiny new systems that require you to change everything. We plug into your existing stack — your CRM, your ticketing system, your data warehouse.',
    icon: '🔗',
  },
  {
    title: 'Transparency Over Complexity',
    description: 'No black boxes. No proprietary methodologies you can\'t understand. You\'ll know exactly what we\'re building, how it works, and how to maintain it.',
    icon: '🔍',
  },
];

const metrics = [
  { value: '127', label: 'Production AI systems deployed' },
  { value: '$12.4M', label: 'Client savings generated' },
  { value: '23 days', label: 'Avg. deployment time' },
  { value: '94%', label: 'Client retention rate' },
];

export const AboutPage: React.FC = () => {
  return (
    <main className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        {/* Animated background blobs */}
        <motion.div
          className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 30, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-violet-500/10 rounded-full blur-[120px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-32">
          <div className="max-w-4xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
            >
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-blue-400"
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-zinc-300 text-sm font-medium">About Us</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-[1.1]"
            >
              We Exist Because Most Companies Are Still{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">
                Waiting for AI
              </span>
              {' '}— While Their Competitors Are Already Deploying It
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="text-xl md:text-2xl text-zinc-400 leading-relaxed"
            >
              We're the team that mid-market companies call when they're done with strategy decks and ready for production AI that actually ships.
            </motion.p>
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Origin Story */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-violet-400"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">The Origin Story</span>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                text: 'Three years ago, we watched a $400M manufacturing company spend',
                highlight: '$1.2 million',
                rest: 'on an "AI transformation initiative."',
              },
              {
                text: 'Eighteen months later, they had a 200-page strategy document, a proof-of-concept that worked in a demo environment, and exactly',
                highlight: 'zero production deployments',
                rest: '.',
              },
              {
                text: 'Meanwhile, a competitor one-third their size deployed three AI agents in under 60 days. Automated their entire customer service queue. Cut response times by 80%.',
                highlight: 'Started winning deals on speed alone.',
                rest: '',
              },
            ].map((paragraph, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
                className="text-lg md:text-xl text-zinc-400 leading-relaxed"
              >
                {paragraph.text}{' '}
                <span className="text-white font-medium">{paragraph.highlight}</span>
                {paragraph.rest}
              </motion.p>
            ))}

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
              className="text-lg md:text-xl text-zinc-400 leading-relaxed"
            >
              The big company had budget. They had buy-in. They had a Big Four consultancy and a dedicated innovation team.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
              className="text-lg md:text-xl text-zinc-400 leading-relaxed"
            >
              What they didn't have was a single person who had actually deployed production AI systems that worked at scale.
            </motion.p>

            {/* Highlight quote */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
              className="relative py-8"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-violet-500/10 to-blue-500/10 rounded-2xl blur-xl" />
              <p className="relative text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">
                That's when we decided to build something different.
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
              className="text-lg md:text-xl text-zinc-400 leading-relaxed"
            >
              Not an AI consultancy. Not a dev shop that "also does AI." A team that only does one thing:{' '}
              <span className="text-white font-medium">deploy production AI systems for mid-market companies</span>
              {' '}— fast, measured, and without the enterprise price tag.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.7, ease: "easeOut" }}
              className="text-xl md:text-2xl text-white font-semibold"
            >
              We've now done it 127 times. The playbook works.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-500/5 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-500/50 via-violet-500/30 to-blue-500/50 rounded-3xl" />
            <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-3xl p-10 md:p-14">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                {metrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <motion.p
                      className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400 mb-3"
                      whileHover={{ scale: 1.05 }}
                    >
                      {metric.value}
                    </motion.p>
                    <p className="text-zinc-400 text-sm">
                      {metric.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="max-w-3xl mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
            >
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-blue-400"
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-zinc-300 text-sm font-medium">Our Philosophy</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white"
            >
              What Makes Us Different
            </motion.h2>
          </div>

          {/* Philosophy Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {philosophyPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
                whileHover={{ y: -5, scale: 1.01 }}
                className="group relative"
              >
                <div className="absolute -inset-[1px] bg-gradient-to-br from-blue-500/20 via-transparent to-violet-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 group-hover:border-zinc-700/50 backdrop-blur-sm h-full transition-colors">
                  <div className="flex items-start gap-4">
                    <motion.div
                      className="w-12 h-12 rounded-xl bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center text-2xl flex-shrink-0"
                      whileHover={{ scale: 1.1, backgroundColor: "rgba(59, 130, 246, 0.2)" }}
                    >
                      {pillar.icon}
                    </motion.div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3">
                        {pillar.title}
                      </h3>
                      <p className="text-zinc-400 leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-gradient-to-r from-blue-600/10 via-violet-600/10 to-blue-600/10 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-blue-400"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">Let's Talk</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            Now You Know Who We Are.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">
              Let's Talk About What We Can Do For You.
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto"
          >
            Book a free strategy session. In 45 minutes, we'll identify the 3-5 highest-impact AI opportunities in your business — ranked by implementation difficulty and dollar impact.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <motion.a
              href="/contact"
              className="group relative inline-flex items-center gap-3 px-10 py-5 bg-white text-zinc-900 font-semibold text-lg rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-white/20"
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Book Your Free Strategy Session
              <motion.svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </motion.svg>
            </motion.a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 text-zinc-500 text-sm"
          >
            We onboard 4 new enterprise clients per month. If you're reading this page, you're already ahead of 90% of your competitors.
          </motion.p>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 flex flex-wrap justify-center gap-8"
          >
            {[
              { icon: '🔒', text: 'NDA Protected' },
              { icon: '⏱️', text: '45-Min Call' },
              { icon: '📊', text: 'Custom Roadmap' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-zinc-500">
                <span>{item.icon}</span>
                <span className="text-sm">{item.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
